import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PreferencesList } from '@/components/PreferencesList';
import { NewClientBadge, ReferencePhotos } from '@/components/BookingExtras';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Search, Users, Loader2, Phone, Mail, Edit, KeyRound, Eye, Trophy, Trash2, Plus, ArrowDownAZ, ArrowUpAZ, Clock } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminClientsSection = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [editClient, setEditClient] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [passwordDialog, setPasswordDialog] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [detailClient, setDetailClient] = useState(null);
  const [clientBookings, setClientBookings] = useState([]);
  const [deleteClient, setDeleteClient] = useState(null);
  const [sortBy, setSortBy] = useState('az');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ first_name: '', last_name: '', email: '', phone: '', password: '' });
  const { getAuthHeaders } = useAuth();

  useEffect(() => { fetchClients(); }, []);
  useEffect(() => { filterClients(); }, [clients, searchTerm, sortBy]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`, { headers: getAuthHeaders() });
      setClients(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const filterClients = () => {
    let result = clients;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      result = result.filter(c =>
        c.first_name?.toLowerCase().includes(s) ||
        c.last_name?.toLowerCase().includes(s) ||
        c.email?.toLowerCase().includes(s) ||
        c.phone?.includes(searchTerm)
      );
    }
    if (sortBy === 'az') result = [...result].sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
    else if (sortBy === 'za') result = [...result].sort((a, b) => `${b.first_name} ${b.last_name}`.localeCompare(`${a.first_name} ${a.last_name}`));
    else if (sortBy === 'recent') result = [...result].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    else if (sortBy === 'oldest') result = [...result].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
    setFilteredClients(result);
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/clients/create`, createForm, { headers: getAuthHeaders() });
      toast.success('Cliente creato!');
      setCreateDialogOpen(false);
      setCreateForm({ first_name: '', last_name: '', email: '', phone: '', password: '' });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const openEdit = (client) => {
    setEditClient(client);
    setEditForm({
      first_name: client.first_name || '',
      last_name: client.last_name || '',
      email: client.email || '',
      phone: client.phone || '',
      loyalty_points: client.loyalty_points || 0,
    });
  };

  const handleSaveEdit = async () => {
    if (!editClient) return;
    try {
      await axios.put(`${API}/admin/clients/${editClient.id}`, editForm, { headers: getAuthHeaders() });
      toast.success('Cliente aggiornato!');
      setEditClient(null);
      fetchClients();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleResetPassword = async () => {
    if (!passwordDialog || !newPassword) return;
    try {
      await axios.put(`${API}/admin/clients/${passwordDialog.id}/reset-password`, { new_password: newPassword }, { headers: getAuthHeaders() });
      toast.success('Password aggiornata!');
      setPasswordDialog(null);
      setNewPassword('');
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleDeleteClient = async () => {
    if (!deleteClient) return;
    try {
      await axios.delete(`${API}/admin/clients/${deleteClient.id}`, { headers: getAuthHeaders() });
      toast.success('Cliente eliminato!');
      setDeleteClient(null);
      fetchClients();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const openDetails = async (client) => {
    setDetailClient(client);
    try {
      const response = await axios.get(`${API}/clients/${client.id}/bookings`, { headers: getAuthHeaders() });
      setClientBookings(response.data);
    } catch (error) {
      setClientBookings([]);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-48"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="clients-section">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xl sm:text-2xl font-bold">Clienti ({clients.length})</h2>
        <Button className="bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setCreateDialogOpen(true)} data-testid="create-client-btn">
          <Plus className="h-4 w-4 mr-1" /> Nuovo
        </Button>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Cerca nome, email o telefono..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 text-base" data-testid="clients-search" />
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[130px]" data-testid="clients-sort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="az">A → Z</SelectItem>
            <SelectItem value="za">Z → A</SelectItem>
            <SelectItem value="recent">Recenti</SelectItem>
            <SelectItem value="oldest">Meno recenti</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filteredClients.length === 0 ? (
        <div className="text-center py-10">
          <Users className="h-10 w-10 text-muted-foreground mx-auto mb-2 opacity-50" />
          <p className="text-sm text-muted-foreground">Nessun cliente trovato</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredClients.map((client) => (
            <Card key={client.id} className="card-elegant" data-testid={`client-${client.id}`}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0" onClick={() => openDetails(client)} role="button">
                    <h3 className="font-semibold text-sm sm:text-base truncate">{client.first_name} {client.last_name}</h3>
                    <div className="flex flex-wrap gap-x-3 text-xs text-muted-foreground mt-0.5">
                      <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{client.phone}</span>
                      <span className="flex items-center gap-1"><Mail className="h-3 w-3" /><span className="truncate max-w-[120px] sm:max-w-none">{client.email}</span></span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">Punti</p>
                      <p className="text-sm font-bold text-accent">{client.loyalty_points || 0}</p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => openEdit(client)} data-testid={`edit-client-${client.id}`}>
                        <Edit className="h-3 w-3 mr-1" /> Modifica
                      </Button>
                      <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => { setPasswordDialog(client); setNewPassword(''); }} data-testid={`reset-pw-${client.id}`}>
                        <KeyRound className="h-3 w-3 mr-1" /> Password
                      </Button>
                      <Button variant="outline" size="sm" className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30" onClick={() => setDeleteClient(client)} data-testid={`delete-client-${client.id}`}>
                        <Trash2 className="h-3 w-3 mr-1" /> Elimina
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Client Dialog */}
      <Dialog open={!!editClient} onOpenChange={(open) => { if (!open) setEditClient(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Modifica Cliente</DialogTitle></DialogHeader>
          {editClient && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nome</Label><Input value={editForm.first_name} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} className="text-base" data-testid="edit-client-fname" /></div>
                <div><Label>Cognome</Label><Input value={editForm.last_name} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} className="text-base" data-testid="edit-client-lname" /></div>
              </div>
              <div><Label>Email</Label><Input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="text-base" /></div>
              <div><Label>Telefono</Label><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="text-base" /></div>
              <div><Label>Punti Fedelta</Label><Input type="number" min="0" value={editForm.loyalty_points} onChange={(e) => setEditForm({ ...editForm, loyalty_points: parseInt(e.target.value) || 0 })} className="text-base" /></div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setEditClient(null)} className="flex-1">Annulla</Button>
                <Button onClick={handleSaveEdit} className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="save-client-edit">Salva</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!passwordDialog} onOpenChange={(open) => { if (!open) setPasswordDialog(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reimposta Password</DialogTitle></DialogHeader>
          {passwordDialog && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Nuova password per <strong>{passwordDialog.first_name} {passwordDialog.last_name}</strong></p>
              <div>
                <Label>Nuova Password</Label>
                <Input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Inserisci nuova password" className="text-base" data-testid="new-password-input" />
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setPasswordDialog(null)} className="flex-1">Annulla</Button>
                <Button onClick={handleResetPassword} className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="confirm-reset-pw">Reimposta</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Client Details Dialog */}
      <Dialog open={!!detailClient} onOpenChange={(open) => { if (!open) setDetailClient(null); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Dettagli Cliente</DialogTitle></DialogHeader>
          {detailClient && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-xs text-muted-foreground">Nome</p><p className="font-semibold">{detailClient.first_name} {detailClient.last_name}</p></div>
                <div><p className="text-xs text-muted-foreground">Punti</p><p className="font-semibold text-accent flex items-center gap-1"><Trophy className="h-3 w-3" />{detailClient.loyalty_points || 0}</p></div>
                <div><p className="text-xs text-muted-foreground">Email</p><p className="font-semibold break-all">{detailClient.email}</p></div>
                <div><p className="text-xs text-muted-foreground">Telefono</p><p className="font-semibold">{detailClient.phone}</p></div>
              </div>
              <div>
                <h3 className="font-semibold text-sm mb-2">Storico Prenotazioni ({clientBookings.length})</h3>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {clientBookings.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-3">Nessuna prenotazione</p>
                  ) : clientBookings.map((b) => (
                    <div key={b.id} className="p-2 bg-secondary rounded-lg text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium">{b.service_name}</span>
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          b.status === 'confirmed' ? 'bg-accent/20 text-accent' :
                          b.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                          'bg-destructive/20 text-destructive'
                        }`}>{b.status === 'confirmed' ? 'OK' : b.status === 'pending' ? 'Attesa' : 'Ann.'}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{format(new Date(b.date), 'dd MMM yyyy', { locale: it })} - {b.time}</p>
                      <NewClientBadge booking={b} small />
                      <PreferencesList preferences={b.client_preferences} compact testId={`client-history-preferences-${b.id}`} />
                      <ReferencePhotos booking={b} headers={getAuthHeaders()} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Client Confirmation */}
      <AlertDialog open={!!deleteClient} onOpenChange={(open) => { if (!open) setDeleteClient(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo cliente?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare <strong>{deleteClient?.first_name} {deleteClient?.last_name}</strong>. Verranno cancellate anche tutte le sue prenotazioni e notifiche. Questa azione non puo essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-client">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteClient} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-client">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Client Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Crea Nuovo Cliente</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateClient} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nome *</Label><Input required value={createForm.first_name} onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })} className="text-base" data-testid="create-client-fname" /></div>
              <div><Label>Cognome *</Label><Input required value={createForm.last_name} onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })} className="text-base" data-testid="create-client-lname" /></div>
            </div>
            <div><Label>Email *</Label><Input type="email" required value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} className="text-base" data-testid="create-client-email" /></div>
            <div><Label>Telefono *</Label><Input required value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} className="text-base" data-testid="create-client-phone" /></div>
            <div><Label>Password *</Label><Input type="password" required value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} className="text-base" data-testid="create-client-password" /></div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)} className="flex-1">Annulla</Button>
              <Button type="submit" className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="submit-create-client">Crea Cliente</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminClientsSection;
